/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toET, todayET } from '../utils/et';
import { format } from 'date-fns';
import EditDriverPanel from '../components/EditDriverPanel';

const today = todayET();

const BH_LEVEL = (d) => {
  const bh201 = d.capabilities?.bh201 ?? d.bh_201_capable ?? false;
  const bh302 = d.capabilities?.bh302 ?? d.bh_302_capable ?? false;
  const calmOnly = d.capabilities?.bhCalmOnly ?? d.bh_calm_only ?? false;
  const avoidAgg = d.capabilities?.avoidAggressive302 ?? d.avoid_aggressive_302 ?? false;

  if (!bh201 && !bh302) return { label: 'No BH', bg: '#f0f0f0', color: '#666' };
  if (bh302 && !avoidAgg && !calmOnly) return { label: 'All BH', bg: '#E1F5EE', color: '#0F6E56' };
  if (bh302 && avoidAgg) return { label: '201 + calm 302', bg: '#E6F1FB', color: '#185FA5' };
  if (bh201 && calmOnly && !bh302) return { label: 'Calm 201 only', bg: '#FAEEDA', color: '#854F0B' };
  if (bh201 && !bh302) return { label: '201 only', bg: '#EEEDFE', color: '#534AB7' };
  return { label: 'No BH', bg: '#f0f0f0', color: '#666' };
};

function Badge({ bg, color, label }) {
  return (
    <span style={{
      background: bg, color, fontSize: 10, padding: '2px 7px',
      borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap'
    }}>{label}</span>
  );
}

function DriverRow({ driver, onCallIn, onRestore, onEdit }) {
  const bh = BH_LEVEL(driver);
  const isAvailable = driver.isAvailable;
  const wheelchair = driver.capabilities?.wheelchair ?? driver.can_do_wheelchair ?? true;
  const sedan = driver.capabilities?.sedan ?? driver.can_do_sedan ?? true;
  const attendantOnly = driver.capabilities?.attendantOnly ?? driver.is_attendant_only ?? false;
  const needsPartner = driver.capabilities?.needsStrongPartner ?? driver.needs_strong_partner ?? false;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', borderBottom: '0.5px solid #f0f0f0',
      background: !isAvailable ? '#fafafa' : 'white',
      opacity: !isAvailable ? 0.7 : 1,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        background: isAvailable ? '#E1F5EE' : '#f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 500,
        color: isAvailable ? '#0F6E56' : '#999',
      }}>
        {(driver.firstName || driver.first_name || '?')[0]}{(driver.lastName || driver.last_name || '?')[0]}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{driver.name || `${driver.first_name} ${driver.last_name}`}</span>
          {(driver.isFullTime || driver.is_full_time) && <Badge bg="#E6F1FB" color="#185FA5" label="FT" />}
          {attendantOnly && <Badge bg="#FAEEDA" color="#854F0B" label="Attendant only" />}
          {!isAvailable && <Badge bg="#FCEBEB" color="#A32D2D" label={driver.availabilityNote || 'Unavailable'} />}
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 4, flexWrap: 'wrap' }}>
          <Badge {...bh} />
          {wheelchair && <Badge bg="#EEEDFE" color="#3C3489" label="WC" />}
          {!sedan && <Badge bg="#f0f0f0" color="#666" label="No sedan" />}
          {needsPartner && <Badge bg="#FAEEDA" color="#854F0B" label="Needs partner" />}
          {driver.requiresPartnerId && <Badge bg="#FAEEDA" color="#854F0B" label="Paired only" />}
        </div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 80 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#185FA5' }}>{driver.weeklyStats?.tripsThisWeek || 0} trips</div>
        <div style={{ fontSize: 11, color: '#999' }}>{parseFloat(driver.weeklyStats?.hoursThisWeek || 0).toFixed(1)} hrs</div>
        <div style={{ fontSize: 11, color: '#999' }}>${driver.hourlyRate || driver.hourly_rate}/hr</div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button onClick={() => onEdit(driver)} style={{ fontSize: 11, padding: '4px 8px' }}>Edit</button>
        {isAvailable ? (
          <button onClick={() => onCallIn(driver)} style={{ fontSize: 11, padding: '4px 8px' }}>Call in</button>
        ) : (
          <button onClick={() => onRestore(driver)} style={{ fontSize: 11, padding: '4px 8px' }}>Restore</button>
        )}
      </div>
    </div>
  );
}

function CallInModal({ driver, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 360, margin: '0 16px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Call in — {driver.name || `${driver.first_name} ${driver.last_name}`}</h2>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>This will mark {driver.firstName || driver.first_name} as unavailable for today.</p>
        <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Reason (optional)</label>
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Sick, personal, etc." style={{ width: '100%', marginBottom: 16 }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
          <button onClick={() => onConfirm(reason)} style={{ fontSize: 13, background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595' }}>Mark unavailable</button>
        </div>
      </div>
    </div>
  );
}

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [callInDriver, setCallInDriver] = useState(null);
  const [editDriver, setEditDriver] = useState(null);
  const [showAddPanel, setShowAddPanel] = useState(false);

  const handleEdit = async (driver) => {
    try {
      const res = await api.get(`/drivers/${driver.id}`);
      setEditDriver(res.data.driver);
    } catch (err) {
      setEditDriver(driver);
    }
  };

  const fetchDrivers = async () => {
    try {
      const res = await api.get(`/drivers/available?date=${today}`);
      setDrivers([...res.data.available, ...res.data.unavailable]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDrivers(); }, []);

  const handleCallIn = async (reason) => {
    try {
      await api.post(`/drivers/${callInDriver.id}/exception`, { exceptionDate: today, exceptionType: 'call_in', reason, requestedBy: 'admin' });
      setCallInDriver(null);
      fetchDrivers();
    } catch (err) {
      alert('Could not mark driver unavailable');
    }
  };

  const handleRestore = async (driver) => {
    try {
      await api.post(`/drivers/${driver.id}/exception`, { exceptionDate: today, exceptionType: 'available_extra', requestedBy: 'admin' });
      fetchDrivers();
    } catch (err) {
      alert('Could not restore driver');
    }
  };

  const filtered = drivers.filter(d => {
    if (filter === 'available') return d.isAvailable;
    if (filter === 'unavailable') return !d.isAvailable;
    if (filter === 'fulltime') return d.isFullTime || d.is_full_time;
    return true;
  });

  const availableCount = drivers.filter(d => d.isAvailable).length;
  const unavailableCount = drivers.filter(d => !d.isAvailable).length;

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Drivers</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
            {availableCount} available · {unavailableCount} unavailable · {format(toET(new Date()), 'EEEE, MMMM d')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchDrivers} style={{ fontSize: 12 }}>Refresh</button>
          <button onClick={() => setShowAddPanel(true)} style={{ fontSize: 12, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '6px 14px' }}>
            + Add driver
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Available today', value: availableCount, color: '#1D9E75' },
          { label: 'Unavailable', value: unavailableCount, color: unavailableCount > 0 ? '#BA7517' : '#666' },
          { label: 'Total roster', value: drivers.length, color: '#185FA5' },
        ].map(s => (
          <div key={s.label} style={{ background: '#f5f5f3', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'All drivers' },
          { key: 'available', label: 'Available' },
          { key: 'unavailable', label: 'Unavailable' },
          { key: 'fulltime', label: 'Full-time' },
          { key: 'schedules', label: 'Schedules' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            fontSize: 12, padding: '4px 12px',
            background: filter === f.key ? '#185FA5' : 'white',
            color: filter === f.key ? 'white' : '#666',
            border: `0.5px solid ${filter === f.key ? '#185FA5' : '#e0e0db'}`,
          }}>
            {f.label}
          </button>
        ))}
      </div>

      {filter === 'schedules' ? (
        <ScheduleManager />
      ) : loading ? (
        <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>
      ) : (
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'hidden' }}>
          {filtered.map(driver => (
            <DriverRow key={driver.id} driver={driver} onCallIn={setCallInDriver} onRestore={handleRestore} onEdit={handleEdit} />
          ))}
        </div>
      )}

      {callInDriver && <CallInModal driver={callInDriver} onClose={() => setCallInDriver(null)} onConfirm={handleCallIn} />}
      {editDriver && <EditDriverPanel driver={editDriver} onClose={() => setEditDriver(null)} onSaved={fetchDrivers} />}
      {showAddPanel && <EditDriverPanel driver={null} onClose={() => setShowAddPanel(false)} onSaved={fetchDrivers} />}
    </div>
  );
}

function formatTime12(t) {
  if (!t) return '--';
  const [h, m] = String(t).split(':').map(Number);
  const ampm = h >= 12 ? 'p' : 'a';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}${ampm}`;
}

function ScheduleManager() {
  const [schedules, setSchedules] = useState([]);
  const [exceptions, setExceptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchSchedules = async () => {
    try {
      const res = await api.get('/drivers/schedules');
      setSchedules(res.data.schedules || []);
      // Fetch upcoming exceptions (all types) for the next 7 days.
      // Map each exception to THIS week's date columns only — an exception
      // for next Tuesday must not bleed into this week's Tuesday column.
      try {
        const excRes = await api.get('/drivers/upcoming-exceptions');
        const byDriver = {};
        // Build the date range for the current week (Mon-Sun)
        const today = new Date();
        const todayET = new Date(today.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        const dayOfWeek = todayET.getDay(); // 0=Sun
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const weekDates = {};
        const dayNames = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
        for (let i = 0; i < 7; i++) {
          const d = new Date(todayET);
          d.setDate(todayET.getDate() + mondayOffset + i);
          const dateStr = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
          weekDates[dateStr] = dayNames[i];
        }
        (excRes.data.exceptions || []).forEach(e => {
          const excDate = e.exception_date.split('T')[0];
          const dayKey = weekDates[excDate];
          if (!dayKey) return; // Exception is outside this week — ignore
          if (!byDriver[e.driver_id]) byDriver[e.driver_id] = {};
          if (!byDriver[e.driver_id][dayKey]) {
            byDriver[e.driver_id][dayKey] = {
              type: e.exception_type,
              from: e.available_from,
              until: e.available_until,
              blocks: Array.isArray(e.blocks) && e.blocks.length ? e.blocks : null,
            };
          }
        });
        setExceptions(byDriver);
      } catch (e) {}
    } catch (e) {} finally { setLoading(false); }
  };

  useEffect(() => { fetchSchedules(); }, []);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const startEdit = (s) => {
    const form = {};
    days.forEach(d => {
      form[`works_${d}`] = s[`works_${d}`] || false;
      form[`${d}_from`] = s[`${d}_from`] ? String(s[`${d}_from`]).substring(0,5) : '';
      form[`${d}_until`] = s[`${d}_until`] ? String(s[`${d}_until`]).substring(0,5) : '';
    });
    form.notes = s.schedule_notes || '';
    setEditForm(form);
    setEditingId(s.id);
  };

  const saveEdit = async (driverId) => {
    try {
      await api.put(`/drivers/${driverId}/schedule`, {
        worksMon: editForm.works_monday, worksTue: editForm.works_tuesday,
        worksWed: editForm.works_wednesday, worksThu: editForm.works_thursday,
        worksFri: editForm.works_friday, worksSat: editForm.works_saturday,
        worksSun: editForm.works_sunday,
        monFrom: editForm.monday_from, monUntil: editForm.monday_until,
        tueFrom: editForm.tuesday_from, tueUntil: editForm.tuesday_until,
        wedFrom: editForm.wednesday_from, wedUntil: editForm.wednesday_until,
        thuFrom: editForm.thursday_from, thuUntil: editForm.thursday_until,
        friFrom: editForm.friday_from, friUntil: editForm.friday_until,
        satFrom: editForm.saturday_from, satUntil: editForm.saturday_until,
        sunFrom: editForm.sunday_from, sunUntil: editForm.sunday_until,
        notes: editForm.notes,
      });
      setEditingId(null);
      fetchSchedules();
    } catch (e) { alert('Save failed'); }
  };

  if (loading) return <div style={{ color: '#999', fontSize: 13 }}>Loading schedules...</div>;

  return (
    <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e0e0db', background: '#f5f5f3' }}>
            <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 500, position: 'sticky', left: 0, background: '#f5f5f3', minWidth: 110 }}>Driver</th>
            {dayLabels.map(d => <th key={d} style={{ padding: '6px 4px', textAlign: 'center', fontSize: 10, fontWeight: 500, minWidth: 70 }}>{d}</th>)}
            <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 500, minWidth: 120 }}>Notes</th>
            <th style={{ padding: '6px 8px', width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {schedules.map(s => editingId === s.id ? (
            <tr key={s.id} style={{ borderBottom: '0.5px solid #f0f0f0', background: '#FAFAF9' }}>
              <td style={{ padding: '6px 10px', fontWeight: 500, position: 'sticky', left: 0, background: '#FAFAF9' }}>{s.first_name} {s.last_name}</td>
              {days.map(d => (
                <td key={d} style={{ padding: '4px', textAlign: 'center' }}>
                  <div><input type="checkbox" checked={editForm[`works_${d}`] || false}
                    onChange={e => setEditForm(f => ({...f, [`works_${d}`]: e.target.checked}))} /></div>
                  {editForm[`works_${d}`] && (
                    <div>
                      <input type="time" value={editForm[`${d}_from`] || ''} onChange={e => setEditForm(f => ({...f, [`${d}_from`]: e.target.value}))} style={{ width: 65, fontSize: 9, padding: 1 }} /><br/>
                      <input type="time" value={editForm[`${d}_until`] || ''} onChange={e => setEditForm(f => ({...f, [`${d}_until`]: e.target.value}))} style={{ width: 65, fontSize: 9, padding: 1 }} />
                    </div>
                  )}
                </td>
              ))}
              <td style={{ padding: '4px 8px' }}>
                <input value={editForm.notes || ''} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} style={{ width: '100%', fontSize: 9, padding: 2 }} />
              </td>
              <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                <button onClick={() => saveEdit(s.id)} style={{ fontSize: 9, padding: '2px 6px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 3, marginBottom: 2 }}>Save</button>
                <button onClick={() => setEditingId(null)} style={{ fontSize: 9, padding: '2px 6px' }}>X</button>
              </td>
            </tr>
          ) : (
            <tr key={s.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
              <td style={{ padding: '6px 10px', fontWeight: 500, position: 'sticky', left: 0, background: 'white' }}>
                {s.first_name} {s.last_name}
                {s.special_notes && <div style={{ fontSize: 9, color: '#854F0B', fontWeight: 400 }}>{s.special_notes.substring(0, 40)}</div>}
                {s.schedule_type === 'flexible' && <div style={{ fontSize: 9, color: '#185FA5', fontWeight: 400 }}>Sets custom hours</div>}
              </td>
              {days.map(d => {
                const works = s[`works_${d}`];
                const from = s[`${d}_from`];
                const until = s[`${d}_until`];
                const exc = exceptions[s.id]?.[d];
                const excOff = exc && ['day_off','call_in','unavailable','leave','training'].includes(exc.type);
                const excAvail = exc && exc.type === 'available_extra';
                const excHours = exc && exc.type === 'hours_modified';

                let bg = '#f9f9f8';
                if (excOff) bg = '#FCEBEB';
                else if (excHours) bg = '#E6F1FB';
                else if (excAvail) bg = '#E6F1FB';
                else if (works) bg = s.schedule_type === 'flexible' ? '#E6F1FB' : '#E1F5EE';

                return (
                  <td key={d} style={{ padding: '4px', textAlign: 'center', background: bg }}>
                    {excOff ? (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 500, color: '#A32D2D' }}>
                          {exc.type === 'day_off' ? 'Off' : exc.type === 'call_in' ? 'Called in' : exc.type === 'partial_day_off' ? 'Out' : exc.type.replace('_',' ')}
                        </div>
                        {exc.type === 'partial_day_off' && (exc.blocks || (exc.from ? [{ from: exc.from, until: exc.until }] : [])).map((b, i) => (
                          <div key={i} style={{ fontSize: 9, color: '#A32D2D' }}>{formatTime12(b.from)}-{formatTime12(b.until)}</div>
                        ))}
                      </div>
                    ) : excHours ? (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 500, color: exc.from ? '#185FA5' : '#A32D2D' }}>{exc.from ? 'Custom' : 'Custom - no hours'}</div>
                        {exc.from
                          ? <div style={{ fontSize: 9, color: '#666' }}>{formatTime12(exc.from)}-{formatTime12(exc.until)}</div>
                          : <div style={{ fontSize: 9, color: '#A32D2D' }}>driver saved blank times</div>}
                      </div>
                    ) : excAvail ? (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 500, color: '#185FA5' }}>Added</div>
                        {exc.from && <div style={{ fontSize: 9, color: '#666' }}>{formatTime12(exc.from)}-{formatTime12(exc.until)}</div>}
                      </div>
                    ) : works ? (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 500, color: s.schedule_type === 'flexible' ? '#185FA5' : '#0F6E56' }}>
                          {s.schedule_type === 'flexible' ? 'Maybe' : 'On'}
                        </div>
                        {from && <div style={{ fontSize: 9, color: '#666' }}>{formatTime12(from)}-{formatTime12(until)}</div>}
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: '#ccc' }}>Off</div>
                    )}
                  </td>
                );
              })}
              <td style={{ padding: '4px 8px', fontSize: 9, color: '#999', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.schedule_notes || ''}
              </td>
              <td style={{ padding: '4px 6px' }}>
                <button onClick={() => startEdit(s)} style={{ fontSize: 9, padding: '2px 6px', color: '#999', background: 'none', border: '0.5px solid #e0e0db', borderRadius: 3 }}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}